import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import JsonLd from '../../components/base/JsonLd';

const RIPPLING_JOBS_URL = 'https://ats.rippling.com/celebrityhh/jobs';

export default function Careers() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://celebrity-homehealth.com';

  const careersJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/careers#webpage`,
      url: `${siteUrl}/careers`,
      name: 'Careers at Celebrity Home Health Las Vegas | Join Our Team',
      description: 'Join one of Las Vegas\'s top home health agencies. We are hiring RNs, LPNs, Physical Therapists, Occupational Therapists, Speech-Language Pathologists, Home Health Aides, and Medical Social Workers.',
      isPartOf: { '@id': `${siteUrl}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Careers', item: `${siteUrl}/careers` },
        ],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      title: 'Home Health Care Professionals — Multiple Positions',
      description: 'Celebrity Home Health is hiring licensed healthcare professionals in Las Vegas, NV including Registered Nurses (RN), Licensed Practical Nurses (LPN), Physical Therapists (PT), Occupational Therapists (OT), Speech-Language Pathologists (SLP), Home Health Aides (HHA), and Medical Social Workers (MSW).',
      datePosted: '2026-01-01',
      employmentType: 'FULL_TIME',
      hiringOrganization: {
        '@type': 'Organization',
        name: 'Celebrity Home Health',
        sameAs: siteUrl,
      },
      jobLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '2121 E Flamingo Rd Ste 218A',
          addressLocality: 'Las Vegas',
          addressRegion: 'NV',
          postalCode: '89119',
          addressCountry: 'US',
        },
      },
      directApply: true,
      url: RIPPLING_JOBS_URL,
      occupationalCategory: ['29-1141', '29-1123', '29-1122', '29-1127', '31-1120'],
    },
  ];

  useEffect(() => {
    document.title = 'Careers at Celebrity Home Health Las Vegas | Join Our Team';
  }, []);

  const benefits = [
    { icon: 'ri-health-book-line', title: 'Health Insurance', desc: 'Comprehensive medical, dental, and vision coverage for you and your family.' },
    { icon: 'ri-calendar-check-line', title: 'Paid Time Off', desc: 'Generous vacation, sick leave, and paid holidays throughout the year.' },
    { icon: 'ri-funds-line', title: '401(k) Plan', desc: 'Retirement savings with competitive company matching contributions.' },
    { icon: 'ri-graduation-cap-line', title: 'Continuing Education', desc: 'Annual allowance for professional development, certifications, and training.' },
    { icon: 'ri-time-line', title: 'Flexible Scheduling', desc: 'Work-life balance with flexible shift options and scheduling flexibility.' },
    { icon: 'ri-car-line', title: 'Mileage Reimbursement', desc: 'Competitive mileage reimbursement for all patient-related travel.' },
    { icon: 'ri-trophy-line', title: 'Recognition Programs', desc: 'Employee of the month, performance bonuses, and career advancement.' },
    { icon: 'ri-team-line', title: 'Supportive Culture', desc: 'A collaborative, inclusive team environment where you are truly valued.' },
  ];

  const openRoles = [
    { title: 'Registered Nurse (RN)', type: 'Full-Time', dept: 'Clinical', icon: 'ri-nurse-line' },
    { title: 'Licensed Practical Nurse (LPN)', type: 'Full-Time', dept: 'Clinical', icon: 'ri-nurse-line' },
    { title: 'Physical Therapist (PT)', type: 'Full-Time', dept: 'Therapy', icon: 'ri-walk-line' },
    { title: 'Physical Therapy Assistant (PTA)', type: 'Part-Time', dept: 'Therapy', icon: 'ri-walk-line' },
    { title: 'Occupational Therapist (OT)', type: 'Full-Time', dept: 'Therapy', icon: 'ri-hand-heart-line' },
    { title: 'Speech-Language Pathologist (SLP)', type: 'Full-Time', dept: 'Therapy', icon: 'ri-chat-voice-line' },
    { title: 'Home Health Aide (HHA)', type: 'Part-Time', dept: 'Personal Care', icon: 'ri-user-heart-line' },
    { title: 'Medical Social Worker (MSW)', type: 'Full-Time', dept: 'Social Services', icon: 'ri-heart-pulse-line' },
  ];

  const whyJoin = [
    { icon: 'ri-heart-pulse-line', title: 'Meaningful Work', desc: 'Make a genuine, lasting impact on patients and families every single day.' },
    { icon: 'ri-team-line', title: 'Great Culture', desc: 'Work alongside passionate, experienced professionals in a supportive team environment.' },
    { icon: 'ri-trophy-line', title: 'Career Growth', desc: 'Clear pathways for advancement with continuous training and mentorship.' },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <JsonLd data={careersJsonLd as Record<string, unknown>[]} id="json-ld-careers" />
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-24 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/1267bb858c0ae21303a7a3e3490d8e44.jpg"
            alt="Join Our Team"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/90 to-slate-900/98"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 border border-amber-400/30 rounded-full text-amber-300 text-xs font-semibold uppercase tracking-wider mb-6">
              <i className="ri-briefcase-line"></i> Now Hiring in Las Vegas, NV
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Build a Career<br />
              That <span className="text-amber-400">Matters</span>
            </h1>
            <p className="text-slate-300 text-xl leading-relaxed mb-10">
              Join one of Las Vegas's top-rated home health agencies. We're looking for compassionate, skilled healthcare professionals who want to make a real difference in people's lives.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={RIPPLING_JOBS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-amber-600 hover:bg-amber-500 text-white rounded-xl font-semibold text-base transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-external-link-line"></i>
                View All Open Positions
              </a>
              <a href="#why-join" className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-white/10 hover:bg-white/15 border border-white/20 text-white rounded-xl font-semibold text-base transition-all duration-300 whitespace-nowrap cursor-pointer">
                Learn More <i className="ri-arrow-down-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Join */}
      <section id="why-join" className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <div className="inline-block px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
              Why Celebrity Home Health
            </div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">More Than Just a Job</h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto">We invest in our people because great care starts with a great team.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {whyJoin.map((item, i) => (
              <div key={i} className="bg-white p-8 rounded-2xl border border-stone-200 text-center">
                <div className="w-14 h-14 bg-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-5">
                  <i className={`${item.icon} text-amber-400 text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NO SOLICITATION + NO FOLLOW-UP POLICY */}
      <section className="py-10 bg-white border-y-2 border-red-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-6">

            {/* No Solicitation */}
            <div className="rounded-2xl border-2 border-red-700 bg-red-950 overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-3 bg-red-700">
                <i className="ri-forbid-2-fill text-white text-xl"></i>
                <span className="text-white font-black text-sm uppercase tracking-widest">Strict No Solicitation Policy</span>
              </div>
              <div className="px-5 py-4 space-y-3">
                <p className="text-red-200 text-sm leading-relaxed">
                  This careers page and all associated communication channels <strong className="text-white">strictly prohibit</strong> unsolicited outreach — including sales, marketing, lead generation, vendor pitches, partnerships, or promotional offers of any kind.
                </p>
                <div>
                  <p className="text-red-300 text-xs font-bold uppercase tracking-wider mb-1.5">Reserved exclusively for:</p>
                  <ul className="space-y-1">
                    {['Qualified job applicants applying for listed positions', 'Verified staffing inquiries directly related to open roles'].map((item) => (
                      <li key={item} className="flex items-center gap-2 text-red-100 text-xs">
                        <i className="ri-checkbox-circle-fill text-red-400 flex-shrink-0"></i>{item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-red-900/60 border border-red-700 rounded-lg px-4 py-2.5">
                  <p className="text-white text-xs font-black uppercase tracking-wide text-center">
                    Unsolicited submissions will be permanently blocked. Repeated violations may result in reporting to regulatory authorities and legal action.
                  </p>
                </div>
              </div>
            </div>

            {/* No Follow-Up Calls Policy */}
            <div className="rounded-2xl border-2 border-amber-600 bg-amber-950 overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-3 bg-amber-700">
                <i className="ri-forbid-line text-white text-xl"></i>
                <span className="text-white font-black text-sm uppercase tracking-widest">Application Follow-Up Policy</span>
              </div>
              <div className="px-5 py-4 space-y-3">
                <p className="text-amber-100 text-sm leading-relaxed">
                  <strong className="text-white">Do not call to inquire about application status.</strong> Our HR team reviews all applications and will contact qualified candidates directly. Unsolicited follow-up calls are not permitted.
                </p>
                <div>
                  <p className="text-amber-300 text-xs font-bold uppercase tracking-wider mb-1.5">Important — please read carefully:</p>
                  <ul className="space-y-1.5">
                    {[
                      'HR will contact you if your qualifications match our needs',
                      'Calling to check application status is not permitted',
                      'Repeated follow-up calls will result in permanent application cancellation',
                      'No exceptions will be made regardless of circumstances',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2 text-amber-100 text-xs">
                        <i className="ri-error-warning-fill text-amber-400 mt-0.5 flex-shrink-0"></i>{item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-amber-900/60 border border-amber-700 rounded-lg px-4 py-2.5">
                  <p className="text-white text-xs font-black uppercase tracking-wide text-center">
                    Submit your application and wait. If selected, HR will reach out. Follow-up calls will permanently disqualify your application — no exceptions.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <div className="inline-block px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
              Open Roles
            </div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Current Openings</h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto">
              We're hiring across multiple disciplines. Apply directly through our careers portal to be considered for current and future openings.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-12">
            {openRoles.map((role, i) => (
              <a
                key={i}
                href={RIPPLING_JOBS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-between p-6 bg-stone-50 hover:bg-amber-50 rounded-2xl border border-stone-200 hover:border-amber-300 transition-all duration-300 cursor-pointer"
              >
                <div className="flex items-center gap-5">
                  <div className="w-12 h-12 bg-white border border-stone-200 group-hover:bg-amber-600 rounded-xl flex items-center justify-center transition-all duration-300 flex-shrink-0">
                    <i className={`${role.icon} text-slate-600 group-hover:text-white text-xl transition-colors duration-300`}></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-base mb-1">{role.title}</h3>
                    <div className="flex items-center gap-3">
                      <span className="text-slate-400 text-xs">{role.dept}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${role.type === 'Full-Time' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                        {role.type}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-amber-600 text-sm font-semibold whitespace-nowrap">
                  Apply <i className="ri-external-link-line"></i>
                </div>
              </a>
            ))}
          </div>

          {/* CTA Banner */}
          <div className="bg-slate-900 rounded-3xl p-10 flex flex-col lg:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">Don't see your role listed?</h3>
              <p className="text-slate-400">Submit a general application through our careers portal and we'll keep your information on file for future opportunities.</p>
            </div>
            <a
              href={RIPPLING_JOBS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-4 bg-amber-600 hover:bg-amber-500 text-white rounded-xl font-semibold text-base transition-all duration-300 whitespace-nowrap cursor-pointer flex-shrink-0"
            >
              <i className="ri-external-link-line"></i>
              Visit Our Careers Portal
            </a>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <div className="inline-block px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
              Total Compensation
            </div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Employee Benefits</h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto">We offer a comprehensive benefits package designed to support your health, wealth, and wellbeing.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {benefits.map((benefit, i) => (
              <div key={i} className="bg-white p-6 rounded-2xl border border-stone-200 hover:border-amber-300 transition-all duration-300">
                <div className="w-11 h-11 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                  <i className={`${benefit.icon} text-amber-700 text-lg`}></i>
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{benefit.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team photo + Apply CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative w-full h-[460px] rounded-3xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=happy-home-health-nurse-in-white-uniform-visiting-smiling-elderly-patient-at-home-genuine-connection-comfortable-bright-home-interior-warm-atmosphere-professional-compassionate-healthcare-worker&width=700&height=460&seq=careers-side-2026&orientation=landscape"
                alt="Working at Celebrity Home Health"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 flex items-center gap-4">
                  <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-2-line text-white"></i>
                  </div>
                  <div>
                    <div className="text-white font-bold text-sm">Las Vegas, NV</div>
                    <div className="text-white/70 text-xs">Henderson · Summerlin · North Las Vegas</div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="inline-block px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-5">
                Ready to Apply?
              </div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6 leading-tight">
                Take the Next Step<br />in Your Career
              </h2>
              <p className="text-slate-500 text-lg mb-6 leading-relaxed">
                Our application process is simple and straightforward. Browse open positions, submit your application, and one of our recruiters will be in touch within 2–3 business days.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { num: '01', label: 'Browse open positions on our careers portal' },
                  { num: '02', label: 'Submit your application and resume online' },
                  { num: '03', label: 'Recruiter review and phone screening' },
                  { num: '04', label: 'Interview with our clinical leadership team' },
                  { num: '05', label: 'Offer, onboarding, and welcome to the team!' },
                ].map((step) => (
                  <div key={step.num} className="flex items-center gap-4">
                    <div className="w-9 h-9 bg-slate-900 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-amber-400 text-xs font-bold">{step.num}</span>
                    </div>
                    <p className="text-slate-600 text-sm">{step.label}</p>
                  </div>
                ))}
              </div>

              <a
                href={RIPPLING_JOBS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-7 py-4 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-semibold text-base transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-external-link-line"></i>
                Apply on Our Careers Portal
              </a>

              <p className="mt-4 text-slate-400 text-sm">
                Questions? Email us at{' '}
                <a href="mailto:info@celebrity-homehealth.com" className="text-amber-600 hover:underline cursor-pointer">
                  info@celebrity-homehealth.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
